import { useEffect, useMemo } from "react";
import { useTranslations } from "@jugl-web/utils";
import {
  useTwilioConversations,
  twilioConversations$,
} from "@jugl-web/rest-api/integrations/hooks/useTwilioConversations";
import { TwilioConversationModel } from "@jugl-web/rest-api/integrations/models/TwilioConversation";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useInView } from "react-intersection-observer";
import { CRMChannel, CRMConversationMode } from "@jugl-web/rest-api";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { TwilioConversationTile } from "../TwilioConversationTile";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useSelectedConversation } from "../../hooks/useSelectedConversation";

export interface TwilioConversationsProps {
  searchQuery: string;
  selectedMode: CRMConversationMode;
}

export const TwilioConversations: React.FC<TwilioConversationsProps> = ({
  searchQuery,
  selectedMode,
}) => {
  const { t } = useTranslations();
  const { selectedConversation, selectConversation, incomingTwilioMessages$ } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { ref: inViewRef, inView } = useInView();
  const { me } = useMe();

  // Compute filter for Twilio based on selected mode
  const computedFilter = useMemo(() => {
    switch (selectedMode) {
      case CRMConversationMode.managedByMe:
        return me?.id || "";
      case CRMConversationMode.waitingResponse:
      case CRMConversationMode.onAIAutoReply:
        return selectedMode;
      default:
        return undefined;
    }
  }, [selectedMode, me?.id]);

  const {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    reset: resetConversations,
    retry,
    loadMore,
  } = useTwilioConversations({
    entityId,
    searchTerm: searchQuery,
    filter: computedFilter,
    pageSize: 10,
  });

  useSelectedConversation({ channel: CRMChannel.twilio });

  // Handle real-time message updates
  useEffect(() => {
    const subscription = incomingTwilioMessages$.subscribe((message) => {
      const conversationExists = conversations.some(
        (c) => c.conv_id === message.conv_id
      );

      if (!conversationExists) {
        // Prepend new conversation to the top
        twilioConversations$.next({
          type: "prepend",
          conversation: {
            contact_info: {
              email: null,
              name: null,
              phone: null,
              info: {},
              rb2b: null,
              conversations: [],
            },
            ...message,
          },
        });
        return;
      }

      const updatedConversation: TwilioConversationModel = {
        contact_info: {
          email: null,
          name: null,
          phone: null,
          info: {},
          rb2b: null,
          conversations: [],
        },
        ...selectedConversation,
        ...message,
      };

      // Update selected conversation if it matches
      if (selectedConversation?.conv_id === message.conv_id) {
        selectConversation(updatedConversation, true);
      }

      // Prepend conversation to bump it to the top
      twilioConversations$.next({
        type: "prepend",
        conversation: updatedConversation,
      });
    });

    return () => subscription.unsubscribe();
  }, [
    incomingTwilioMessages$,
    conversations,
    selectedConversation,
    selectConversation,
    resetConversations,
  ]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  // Empty state content
  const emptyStateContent = useMemo(() => {
    if (conversations.length === 0 && !searchQuery) {
      return (
        <div className="mx-auto mt-40 flex w-full max-w-[340px] flex-col items-center justify-center gap-2 p-5 text-center">
          {t({
            id: "client-support-page.you-will-get-notification-once-receive-message",
            defaultMessage:
              "You will get a notification once you receive message from Clients 🙌",
          })}
        </div>
      );
    }

    if (conversations.length === 0) {
      return (
        <span className="font-secondary mt-10 text-center text-sm leading-[160%] text-[#828282]">
          {t({
            id: "client-support-page.no-results",
            defaultMessage: "No results 😔",
          })}
        </span>
      );
    }

    return null;
  }, [conversations.length, searchQuery, t]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading}
      onRetry={retry}
      isLoadingMore={isLoadingMore}
      isLoadingMoreError={isLoadingMoreError}
    >
      {conversations.map((conversation) => (
        <TwilioConversationTile
          key={conversation.conv_id}
          conversation={conversation}
          searchQuery={searchQuery}
        />
      ))}
      {hasMore && !isLoading && !isLoadingMore && <div ref={inViewRef} />}
      {emptyStateContent}
    </DataLoadingWrapper>
  );
};
